/**
 * Generates a unique identifier using the Web Crypto API when available,
 * with a fallback for older browsers or non-secure contexts.
 * 
 * @returns {string} A UUID v4 formatted string (e.g., "550e8400-e29b-41d4-a716-446655440000")
 * 
 * @example
 * ```ts
 * const userId = generateId();
 * const cartItemId = generateId();
 * ```
 * 
 * Browser Support:
 * - Modern browsers (Chrome 92+, Firefox 95+, Safari 15.4+): Uses crypto.randomUUID()
 * - Older browsers: Uses RFC4122-compliant UUID v4 fallback
 * - Non-HTTPS contexts: Automatically uses fallback
 */
export const generateId = (): string => {
  // Try to use native crypto.randomUUID if available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback: Generate RFC4122 version 4 compliant UUID
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generates a short, non-standard ID for cases where UUID format is not required.
 * Less collision-resistant than generateId(), use only for temporary/non-critical IDs.
 * 
 * @returns {string} A short random string (e.g., "k3x9f2p")
 * 
 * @example
 * ```ts
 * const tempSessionId = generateShortId();
 * ```
 */
export const generateShortId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};
